import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { CalendarDays, Check, ChevronLeft, ChevronRight, CloudOff, Search, Send, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { checkServiceAvailability } from '../../services/functionsService';
import { findClientByIdentity, saveClientWithUniqueCpf } from '../../services/clientService';
import { formatCpf, isValidCpf } from '../../utils/cpf';
import { buildPublicVoucherData, buildVoucherLink, buildVoucherWhatsAppLink, createVoucherToken } from '../../utils/voucher';
import LoadingState from '../shared/LoadingState';
import './CollaboratorScheduler.css';

const serviceLabels = {
  boat: 'Lancha',
  schooner: 'Escuna',
  inn: 'Pousada',
  pousada: 'Pousada',
  tour: 'Passeio',
  transfer: 'Transfer',
  experience: 'Experiência',
  restaurant: 'Restaurante',
  bar: 'Bar',
  snack_bar: 'Lanchonete'
};

const getServiceType = (service) => {
  if (service?.isPousada || service?.type === 'inn' || service?.type === 'pousada') return 'Pousada';
  return serviceLabels[service?.type] || service?.category || 'Serviço';
};

const cacheKey = (companyId, name) => `sdc.scheduler.${companyId}.${name}`;

const activeStatuses = ['pending', 'confirmed'];

const toDate = (value) => {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateKey = (value) => {
  const date = toDate(value);
  if (!date) return '';
  return date.toISOString().slice(0, 10);
};

const getServiceCapacity = (service) => {
  if (!service) return 0;
  if ((service.isPousada || service.type === 'inn' || service.type === 'pousada') && Array.isArray(service.rooms)) {
    return service.rooms.reduce((sum, room) => sum + (Number(room.capacity) || 0), 0);
  }
  return Number(service.capacity) || 0;
};

const getLocalAvailability = (service, dateKey, reservations) => {
  const totalCapacity = getServiceCapacity(service);
  if (!service || !dateKey || !totalCapacity) {
    return { totalCapacity, bookedCapacity: 0, remainingCapacity: totalCapacity, source: 'local' };
  }

  const bookedCapacity = reservations
    .filter(reservation => (
      reservation.serviceId === service.id &&
      activeStatuses.includes(reservation.status) &&
      toDateKey(reservation.date || reservation.checkInDate) === dateKey
    ))
    .reduce((sum, reservation) => sum + (Number(reservation.participants) || 1), 0);

  return {
    totalCapacity,
    bookedCapacity,
    remainingCapacity: Math.max(0, totalCapacity - bookedCapacity),
    source: 'local'
  };
};

const CollaboratorScheduler = () => {
  const { userData } = useAuth();
  const companyId = userData?.role === 'collaborator' ? userData?.currentCompanyId : userData?.companyId;
  const [company, setCompany] = useState(null);
  const [services, setServices] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedRoomIndex, setSelectedRoomIndex] = useState('');
  const [search, setSearch] = useState('');
  const [availabilityByService, setAvailabilityByService] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isOffline, setIsOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [lastAction, setLastAction] = useState('');
  const [mobileStep, setMobileStep] = useState(1);
  const [isMobileFlow, setIsMobileFlow] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 680px)').matches
  ));
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    cpf: '',
    date: '',
    time: '',
    checkIn: '',
    checkOut: '',
    participants: 1,
    notes: ''
  });

  const loadData = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);

    try {
      const companySnap = await getDoc(doc(db, 'companies', companyId));
      const companyData = companySnap.exists() ? { id: companySnap.id, ...companySnap.data() } : null;
      setCompany(companyData);
      localStorage.setItem(cacheKey(companyId, 'company'), JSON.stringify(companyData));

      const servicesSnap = await getDocs(collection(db, `companies/${companyId}/services`));
      const servicesData = servicesSnap.docs.map(item => ({ id: item.id, ...item.data() }));
      setServices(servicesData);
      localStorage.setItem(cacheKey(companyId, 'services'), JSON.stringify(servicesData));

      const reservationsRef = collection(db, `companies/${companyId}/reservations`);
      const reservationsQuery = userData?.role === 'collaborator' && userData?.email
        ? query(reservationsRef, where('createdByEmail', '==', userData.email))
        : query(reservationsRef, where('status', 'in', activeStatuses));
      const reservationsSnap = await getDocs(reservationsQuery);
      const reservationsData = reservationsSnap.docs
        .map(item => ({ id: item.id, ...item.data() }))
        .filter(reservation => activeStatuses.includes(reservation.status));
      setReservations(reservationsData);
      localStorage.setItem(cacheKey(companyId, 'reservations'), JSON.stringify(reservationsData));
      localStorage.removeItem(cacheKey(companyId, 'hasPendingWrites'));
    } catch (error) {
      const cachedCompany = localStorage.getItem(cacheKey(companyId, 'company'));
      const cachedServices = localStorage.getItem(cacheKey(companyId, 'services'));
      const cachedReservations = localStorage.getItem(cacheKey(companyId, 'reservations'));
      if (cachedCompany) setCompany(JSON.parse(cachedCompany));
      if (cachedServices) setServices(JSON.parse(cachedServices));
      if (cachedReservations) setReservations(JSON.parse(cachedReservations));
      setLastAction('Modo offline: exibindo a última carga salva neste aparelho.');
    } finally {
      setLoading(false);
    }
  }, [companyId, userData?.email, userData?.role]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const updateStatus = () => setIsOffline(!navigator.onLine);
    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 680px)');
    const updateFlowMode = (event) => setIsMobileFlow(event.matches);
    mediaQuery.addEventListener('change', updateFlowMode);
    return () => mediaQuery.removeEventListener('change', updateFlowMode);
  }, []);

  useEffect(() => {
    if (!isOffline && companyId && localStorage.getItem(cacheKey(companyId, 'hasPendingWrites'))) {
      setLastAction('Conexão voltou. O Firebase sincronizará as alterações pendentes automaticamente.');
      loadData();
    }
  }, [isOffline, companyId, loadData]);

  const filteredServices = useMemo(() => {
    const term = search.toLowerCase();
    return services.filter(service => {
      return !term ||
        service.name?.toLowerCase().includes(term) ||
        service.description?.toLowerCase().includes(term) ||
        getServiceType(service).toLowerCase().includes(term);
    });
  }, [services, search]);

  const isPousada = selectedService?.isPousada || selectedService?.type === 'inn' || selectedService?.type === 'pousada';
  const selectedRoom = isPousada && selectedRoomIndex !== '' ? selectedService?.rooms?.[Number(selectedRoomIndex)] : null;
  const selectedDateKey = isPousada ? form.checkIn : form.date;

  useEffect(() => {
    if (!companyId || !selectedDateKey || services.length === 0) {
      setAvailabilityByService({});
      return;
    }

    const localAvailability = services.reduce((acc, service) => ({
      ...acc,
      [service.id]: getLocalAvailability(service, selectedDateKey, reservations)
    }), {});
    setAvailabilityByService(localAvailability);

    if (isOffline) return;

    let cancelled = false;
    const loadAvailability = async () => {
      setLoadingAvailability(true);
      const entries = await Promise.all(services.map(async (service) => {
        const { data, error } = await checkServiceAvailability({
          companyId,
          serviceId: service.id,
          date: `${selectedDateKey}T12:00:00`,
          dayOnly: true
        });

        if (error || !data) {
          return [service.id, localAvailability[service.id]];
        }

        return [service.id, { ...data, source: 'online' }];
      }));

      if (!cancelled) {
        setAvailabilityByService(Object.fromEntries(entries));
        setLoadingAvailability(false);
      }
    };

    loadAvailability();

    return () => {
      cancelled = true;
    };
  }, [companyId, isOffline, reservations, selectedDateKey, services]);

  const totalAmount = useMemo(() => {
    if (!selectedService) return 0;
    if (isPousada && selectedRoom) {
      const start = form.checkIn ? new Date(`${form.checkIn}T12:00`) : null;
      const end = form.checkOut ? new Date(`${form.checkOut}T12:00`) : null;
      const days = start && end ? Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24))) : 1;
      return (Number(selectedRoom.price) || 0) * days;
    }
    return (Number(selectedService.price) || 0) * (Number(form.participants) || 1);
  }, [form.checkIn, form.checkOut, form.participants, isPousada, selectedRoom, selectedService]);

  const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

  const focusMobileFlow = () => {
    window.requestAnimationFrame(() => {
      document.querySelector('.scheduler-mobile-wizard')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const setMobileFlowStep = (step) => {
    setLastAction('');
    setMobileStep(step);
    focusMobileFlow();
  };

  const advanceMobileFlow = () => {
    if (mobileStep === 1) {
      if (!selectedService) {
        setLastAction('Selecione um serviço para continuar.');
        return;
      }
      if (!selectedDateKey) {
        setLastAction('Informe a data desejada para continuar.');
        return;
      }
    }

    if (mobileStep === 2 && (!form.name.trim() || !form.phone.trim() || !form.cpf.trim())) {
      setLastAction('Informe o nome, o CPF e o WhatsApp do cliente para continuar.');
      return;
    }
    if (mobileStep === 2 && !isValidCpf(form.cpf)) {
      setLastAction('Informe um CPF válido para continuar.');
      return;
    }
    if (mobileStep === 2 && form.email && !/^\S+@\S+\.\S+$/.test(form.email)) {
      setLastAction('Informe um e-mail válido ou deixe o campo vazio.');
      return;
    }

    if (mobileStep === 3) {
      if (isPousada && (!form.checkIn || !form.checkOut || selectedRoomIndex === '')) {
        setLastAction('Selecione o quarto e informe check-in e check-out.');
        return;
      }
      if (isPousada && new Date(form.checkOut) <= new Date(form.checkIn)) {
        setLastAction('O check-out precisa ser posterior ao check-in.');
        return;
      }
      if (!isPousada && (!form.date || !form.time)) {
        setLastAction('Informe a data e o horário da reserva.');
        return;
      }

      const selectedAvailability = availabilityByService[selectedService.id];
      if (!isOffline && selectedAvailability?.totalCapacity > 0 && Number(form.participants) > selectedAvailability.remainingCapacity) {
        setLastAction(`Lotação excedida. Restam ${selectedAvailability.remainingCapacity} vaga(s) para este serviço.`);
        return;
      }
    }

    setMobileFlowStep(Math.min(4, mobileStep + 1));
  };

  const resetForm = () => {
    setForm({ name: '', phone: '', email: '', cpf: '', date: '', time: '', checkIn: '', checkOut: '', participants: 1, notes: '' });
    setSelectedRoomIndex('');
  };

  const createReservation = async (event) => {
    event.preventDefault();
    if (!companyId || !selectedService) return;
    if (!form.name || !form.phone || !form.cpf) {
      setLastAction('Informe nome, CPF e WhatsApp do cliente.');
      return;
    }
    if (!isValidCpf(form.cpf)) {
      setLastAction('Informe um CPF válido.');
      return;
    }
    if (isPousada && (!form.checkIn || !form.checkOut || selectedRoomIndex === '')) {
      setLastAction('Selecione quarto, check-in e check-out.');
      return;
    }
    if (!isPousada && (!form.date || !form.time)) {
      setLastAction('Informe data e horário da reserva.');
      return;
    }
    const selectedAvailability = availabilityByService[selectedService.id];
    if (!isOffline && selectedAvailability?.totalCapacity > 0 && Number(form.participants) > selectedAvailability.remainingCapacity) {
      setLastAction(`Lotação excedida. Restam ${selectedAvailability.remainingCapacity} vaga(s) para este serviço na data selecionada.`);
      return;
    }

    setSaving(true);
    try {
      const existingClient = await findClientByIdentity({ companyId, cpf: form.cpf, email: form.email });
      const clientRef = existingClient || await saveClientWithUniqueCpf({ companyId, data: {
        name: form.name,
        phone: form.phone,
        email: form.email,
        cpf: form.cpf,
        totalReservations: 0,
        totalSpent: 0,
        createdBy: userData?.email || '',
        createdByEmail: userData?.email || ''
      } });

      const reservationRef = doc(collection(db, `companies/${companyId}/reservations`));
      const token = createVoucherToken();
      const date = isPousada ? new Date(`${form.checkIn}T12:00`) : new Date(`${form.date}T${form.time}`);
      const reservation = {
        clientId: clientRef.id,
        clientName: form.name,
        clientEmail: form.email,
        clientPhone: form.phone,
        clientCpf: clientRef.cpf,
        serviceId: selectedService.id,
        serviceName: selectedService.name || 'Serviço',
        commissionRate: Number(selectedService.commission) || 0,
        roomId: selectedRoomIndex,
        roomName: selectedRoom?.name || '',
        date,
        participants: Number(form.participants) || 1,
        notes: form.notes,
        status: 'confirmed',
        paymentStatus: 'unpaid',
        totalAmount,
        paidAmount: 0,
        voucherToken: token,
        checkInStatus: 'pending',
        checkOutStatus: 'pending',
        companyId,
        companyName: company?.name || '',
        createdBy: userData?.name || userData?.email || 'Colaborador',
        createdByEmail: userData?.email || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (isPousada) {
        const checkInDate = new Date(`${form.checkIn}T12:00`);
        const checkOutDate = new Date(`${form.checkOut}T12:00`);
        reservation.checkInDate = checkInDate;
        reservation.checkOutDate = checkOutDate;
        reservation.dailyCount = Math.max(1, Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24)));
      }

      await setDoc(reservationRef, reservation);
      await setDoc(doc(db, 'publicVouchers', token), buildPublicVoucherData({
        token,
        companyId,
        companyData: company,
        reservationId: reservationRef.id,
        reservation
      }));

      localStorage.setItem(cacheKey(companyId, 'hasPendingWrites'), 'true');
      setReservations(prev => [{ id: reservationRef.id, ...reservation }, ...prev]);
      const voucherLink = buildVoucherLink({ token });
      const whatsappLink = buildVoucherWhatsAppLink({
        phone: form.phone,
        companyName: company?.name,
        reservation: { id: reservationRef.id, ...reservation },
        voucherLink
      });

      resetForm();
      if (isMobileFlow) setMobileStep(1);
      setLastAction(isOffline ? 'Reserva salva neste aparelho. Será sincronizada quando a conexão voltar.' : 'Reserva criada e voucher pronto para envio.');
      if (whatsappLink) window.open(whatsappLink, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Erro ao criar reserva:', error);
      setLastAction(error?.message || 'Erro ao criar reserva.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState title="Carregando agendamento" subtitle="Baixando servicos e agenda para uso offline" />;
  }

  return (
    <main className="scheduler-page">
      <section className="scheduler-hero" style={{ background: `linear-gradient(135deg, ${company?.primaryColor || '#2563eb'}, ${company?.secondaryColor || '#0f766e'})` }}>
        <div className="scheduler-brand">
          <div className="scheduler-logo">{company?.logoUrl ? <img src={company.logoUrl} alt={company?.name || 'Logo'} /> : <span>{company?.name?.charAt(0) || 'S'}</span>}</div>
          <div>
            <p>Agendamento do colaborador</p>
            <h1>{company?.name || userData?.companyName || 'Empresa'}</h1>
          </div>
        </div>
        <div className="scheduler-hero__meta">
          <span>{services.length} serviço(s)</span>
          <span>{reservations.length} reserva(s) ativas</span>
        </div>
      </section>

      {(isOffline || localStorage.getItem(cacheKey(companyId, 'hasPendingWrites'))) && (
        <div className="scheduler-offline">
          <CloudOff size={18} />
          <span>Você está offline ou possui alterações ainda não sincronizadas. A agenda pode ter alterações recentes.</span>
        </div>
      )}
      {lastAction && <div className="scheduler-message">{lastAction}</div>}

      {isMobileFlow && (
        <nav className="scheduler-mobile-wizard" aria-label="Etapas do agendamento">
          {['Serviço', 'Cliente', 'Detalhes', 'Confirmar'].map((label, index) => {
            const step = index + 1;
            return (
              <div key={label} className={`scheduler-mobile-step ${mobileStep === step ? 'is-current' : ''} ${mobileStep > step ? 'is-complete' : ''}`}>
                <span>{mobileStep > step ? <Check size={14} /> : step}</span>
                <small>{label}</small>
              </div>
            );
          })}
        </nav>
      )}

      <section className="scheduler-layout">
        {(!isMobileFlow || mobileStep === 1) && <div className="scheduler-services">
          <div className="scheduler-section-title">
            <h2>Serviços</h2>
            <div className="scheduler-search"><Search size={17} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar serviço" /></div>
          </div>
          <div className="scheduler-date-filter">
            <CalendarDays size={17} />
            <label>
              Data para consultar vagas
              <input type="date" value={selectedDateKey} onChange={(e) => setForm({ ...form, date: e.target.value, checkIn: e.target.value })} />
            </label>
            {loadingAvailability && <span>Atualizando vagas...</span>}
          </div>
          <div className="scheduler-service-list">
            {filteredServices.map(service => {
              const availability = availabilityByService[service.id] || getLocalAvailability(service, selectedDateKey, reservations);
              const hasCapacity = availability.totalCapacity > 0;

              return (
                <button key={service.id} className={`scheduler-service ${selectedService?.id === service.id ? 'is-selected' : ''}`} onClick={() => { setSelectedService(service); setSelectedRoomIndex(''); }} type="button">
                  {service.imageUrl && <img src={service.imageUrl} alt={service.name} />}
                  <div>
                    <span>{getServiceType(service)}</span>
                    <strong>{service.name}</strong>
                    <p>{service.description || 'Sem descrição cadastrada.'}</p>
                    <div className="scheduler-capacity">
                      {selectedDateKey && hasCapacity ? (
                        <>
                          <small>{availability.remainingCapacity} vaga(s)</small>
                          <small>{availability.bookedCapacity}/{availability.totalCapacity} ocupadas</small>
                        </>
                      ) : hasCapacity ? (
                        <small>Lotação: {availability.totalCapacity}</small>
                      ) : (
                        <small>Lotação não cadastrada</small>
                      )}
                    </div>
                  </div>
                  <b>{formatCurrency(service.price || 0)}</b>
                </button>
              );
            })}
          </div>
          {isMobileFlow && (
            <button className="scheduler-next" type="button" onClick={advanceMobileFlow}>
              Continuar <ChevronRight size={18} />
            </button>
          )}
        </div>}

        {(!isMobileFlow || mobileStep > 1) && <form className="scheduler-form" onSubmit={createReservation}>
          <div className="scheduler-section-title">
            <h2>{isMobileFlow ? ['','', 'Cliente', 'Detalhes', 'Confirmar reserva'][mobileStep] : 'Reserva'}</h2>
            <span>{selectedService ? selectedService.name : 'Selecione um serviço'}</span>
          </div>

          {(!isMobileFlow || mobileStep === 2) && (
            <div className="scheduler-form-stage">
              <label>Cliente<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></label>
              <label>CPF<input inputMode="numeric" maxLength="14" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: formatCpf(e.target.value) })} required /></label>
              <label>WhatsApp<input inputMode="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required /></label>
              <label>E-mail<input type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            </div>
          )}

          {(!isMobileFlow || mobileStep === 3) && (
            <div className="scheduler-form-stage">
              {isPousada ? (
                <>
                  <label>Quarto<select value={selectedRoomIndex} onChange={(e) => setSelectedRoomIndex(e.target.value)} required>
                    <option value="">Selecione</option>
                    {(selectedService?.rooms || []).map((room, index) => <option key={`${room.name}-${index}`} value={index}>{room.name} - {formatCurrency(room.price || 0)}</option>)}
                  </select></label>
                  <div className="scheduler-two-cols">
                    <label>Check-in<input type="date" value={form.checkIn} onChange={(e) => setForm({ ...form, checkIn: e.target.value })} required /></label>
                    <label>Check-out<input type="date" value={form.checkOut} min={form.checkIn} onChange={(e) => setForm({ ...form, checkOut: e.target.value })} required /></label>
                  </div>
                </>
              ) : (
                <div className="scheduler-two-cols">
                  <label>Data<input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></label>
                  <label>Horário<input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required /></label>
                </div>
              )}

              <label>Participantes<input type="number" min="1" value={form.participants} onChange={(e) => setForm({ ...form, participants: Number(e.target.value) || 1 })} required /></label>
              <label>Observações<textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="3" /></label>
            </div>
          )}

          {(!isMobileFlow || mobileStep === 4) && (
            <div className="scheduler-form-stage scheduler-confirmation">
              {isMobileFlow && (
                <div className="scheduler-booking-summary">
                  <div><span>Serviço</span><strong>{selectedService?.name || '-'}</strong></div>
                  <div><span>Cliente</span><strong>{form.name || '-'}</strong></div>
                  <div><span>CPF</span><strong>{form.cpf || '-'}</strong></div>
                  <div><span>WhatsApp</span><strong>{form.phone || '-'}</strong></div>
                  <div><span>{isPousada ? 'Período' : 'Data e horário'}</span><strong>{isPousada ? `${form.checkIn} a ${form.checkOut}` : `${form.date} ${form.time}`}</strong></div>
                  <div><span>Participantes</span><strong>{form.participants}</strong></div>
                </div>
              )}
              <div className="scheduler-total">
                <span>Total</span>
                <strong>{formatCurrency(totalAmount)}</strong>
              </div>
              {selectedService && selectedDateKey && (
                <div className="scheduler-selected-capacity">
                  {(() => {
                    const availability = availabilityByService[selectedService.id] || getLocalAvailability(selectedService, selectedDateKey, reservations);
                    return availability.totalCapacity > 0
                      ? `${availability.remainingCapacity} vaga(s) disponíveis em ${selectedDateKey}`
                      : 'Lotação total não cadastrada para este serviço.';
                  })()}
                </div>
              )}
              <button className="scheduler-submit" disabled={!selectedService || saving} type="submit">
                {saving ? <Check size={18} /> : <Send size={18} />}
                {saving ? 'Salvando...' : 'Finalizar e enviar voucher'}
              </button>
            </div>
          )}

          {isMobileFlow && mobileStep < 4 && (
            <div className="scheduler-mobile-actions">
              <button className="scheduler-back" type="button" onClick={() => setMobileFlowStep(mobileStep - 1)}>
                <ChevronLeft size={18} /> Voltar
              </button>
              <button className="scheduler-next" type="button" onClick={advanceMobileFlow}>
                Continuar <ChevronRight size={18} />
              </button>
            </div>
          )}
          {isMobileFlow && mobileStep === 4 && (
            <button className="scheduler-back scheduler-back--full" type="button" onClick={() => setMobileFlowStep(3)}>
              <ChevronLeft size={18} /> Voltar e editar
            </button>
          )}
        </form>}
      </section>

      <section className="scheduler-reservations">
        <h2><CalendarDays size={20} /> Reservas ativas</h2>
        <div className="scheduler-reservation-grid">
          {reservations.slice(0, 12).map(reservation => (
            <div key={reservation.id} className="scheduler-reservation">
              <strong>{reservation.clientName}</strong>
              <span>{reservation.serviceName}</span>
              <small><Users size={14} /> {reservation.participants || 1} participante(s)</small>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default CollaboratorScheduler;
